import { defineStore } from "pinia";

import telemetryTrackersApi from "@/services/api/telemetry/telemetryTrackersApi";
import telemetryVehiclesApi from "@/services/api/telemetry/telemetryVehiclesApi";
import telemetryDailyStatsApi from "@/services/api/telemetry/telemetryDailyStatsApi";
import telemetryPositionsApi from "@/services/api/telemetry/telemetryPositionsApi";

import { useNotificationStore } from "@/stores/notificationStore";
import { getCompanyId } from "@/utils/userUtils";
import { sumMileageForRange } from "@/utils/telemetryUtils";

/**
 * Конфигурация уровней активности по километражу.
 * Используется в календаре и легенде.
 * @type {Array<{ min: number, max: number|null, cssClass: string, color: string, label: string }>}
 */
export const ACTIVITY_LEVELS = [
  {
    min: 0,
    max: 0,
    cssClass: "day-cell--none",
    color: "#f5f5f5",
    label: "0 км",
  },
  {
    min: 1,
    max: 99,
    cssClass: "day-cell--low",
    color: "#d4edda",
    label: "1–99 км",
  },
  {
    min: 100,
    max: 299,
    cssClass: "day-cell--medium",
    color: "#a8d5b5",
    label: "100–299 км",
  },
  {
    min: 300,
    max: 599,
    cssClass: "day-cell--high",
    color: "#f0c060",
    label: "300–599 км",
  },
  {
    min: 600,
    max: 799,
    cssClass: "day-cell--very-high",
    color: "#e87b30",
    label: "600–799 км",
  },
  {
    min: 800,
    max: null,
    cssClass: "day-cell--max",
    color: "#c0392b",
    label: "800+ км",
  },
];

/**
 * Возвращает CSS-класс ячейки по километражу.
 * @param {number|null} km
 * @returns {string}
 */
export function getMileageClass(km) {
  if (km === null || km === undefined) return "";
  const level = ACTIVITY_LEVELS.find(
    (l) => km >= l.min && (l.max === null || km <= l.max)
  );
  return level ? level.cssClass : "";
}

function normalizeFleetVehicle(item) {
  const vehicle = item?.vehicle || {};
  const tracker = item?.tracker || null;
  const currentPosition = item?.current_position || null;
  const activeOrder = item?.active_order || null;
  const activeDriver = item?.active_driver || null;

  return {
    ...vehicle,
    telemetry_tracker: tracker,
    telemetry_current_position: currentPosition,
    freshness_status: item?.freshness_status || null,
    current_day_mileage: item?.current_day_mileage ?? null,
    current_day_mileage_status: item?.current_day_mileage_status ?? "no_target",
    active_order: activeOrder,
    active_driver: activeDriver,
  };
}

export const useTelemetryStore = defineStore("telemetryStore", {
  state: () => ({
    trackers: [],
    vehicles: [],
    monitoringSync: {
      phase: "idle",
      isRefreshing: false,
      lastSyncedAt: null,
      liveStatus: "connecting",
    },
    companyId: getCompanyId(),
    isLoading: false,
    error: null,

    calendarStats: {},
    calendarLoading: false,

    routePositions: [],
    excludedRoutePositions: [],
    routeLoading: false,
    routeVehicle: null,
    routeDateFrom: null,
    routeDateTo: null,
    routeDiagnosticMode: false,
    routeBulkUpdateSubmitting: false,

    selectVehicles: [],
    selectVehiclesPagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },
    selectVehiclesLoading: false,
    selectVehiclesSearchQuery: "",
    // Мобильный ввод запускает несколько запросов подряд. Версия не дает
    // запоздавшему ответу от промежуточного запроса заменить свежий список.
    selectVehiclesRequestVersion: 0,
  }),

  getters: {
    /**
     * Машины у которых есть текущая позиция телеметрии.
     * @param {Object} state
     * @returns {Array}
     */
    vehiclesWithPosition(state) {
      return state.vehicles.filter(
        (v) => v.telemetry_current_position !== null
      );
    },

    /**
     * Суммарный пробег за выбранный период маршрута
     * на основе calendarStats из стора.
     * Избавляет компоненты от локального confirmedCalendarStats.
     * @param {Object} state
     * @returns {number}
     */
    routeMileage(state) {
      return sumMileageForRange(
        state.calendarStats,
        state.routeDateFrom,
        state.routeDateTo
      );
    },
  },

  actions: {
    async getTrackersRequest(params = {}) {
      this.isLoading = true;
      try {
        const response = await telemetryTrackersApi.index(
          this.companyId,
          params
        );
        this.trackers = response.data.result || [];
      } catch (error) {
        this.error = error;
        console.error("Ошибка при получении трекеров:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async createTracker(payload) {
      this.isLoading = true;
      try {
        await telemetryTrackersApi.create(this.companyId, payload);
        await this.getTrackersRequest();
      } catch (error) {
        console.error("Ошибка при создании трекера:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateTracker(id, payload) {
      this.isLoading = true;
      try {
        await telemetryTrackersApi.update(
          this.companyId,
          id,
          payload
        );
        await this.getTrackersRequest();
      } catch (error) {
        console.error("Ошибка при обновлении трекера:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async trackerEnable(id) {
      this.isLoading = true;
      try {
        await telemetryTrackersApi.enable(this.companyId, id);
        await this.getTrackersRequest();
      } catch (error) {
        console.error("Ошибка при включении трекера:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async trackerDisable(id) {
      this.isLoading = true;
      try {
        await telemetryTrackersApi.disable(this.companyId, id);
        await this.getTrackersRequest();
      } catch (error) {
        console.error("Ошибка при отключении трекера:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async trackerSetPrimary(id) {
      this.isLoading = true;
      try {
        await telemetryTrackersApi.setPrimary(this.companyId, id);
        await this.getTrackersRequest();
      } catch (error) {
        console.error(
          "Ошибка при установке основного трекера:",
          error
        );
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    setMonitoringSync(state) {
      this.monitoringSync = state;
    },

    // Не включает общий loader и не очищает маршрут при фоновой сверке.
    async refreshVehicles({ signal, params = {} } = {}) {
      const companyId = this.companyId;
      const positionsAtStart = new Map(this.vehicles.map((vehicle) => [
        vehicle.id, vehicle.telemetry_current_position,
      ]));
      const response = await telemetryVehiclesApi.index(companyId, params, {
        signal,
        timeout: 20_000,
        skipErrorNotification: true,
      });
      if (signal?.aborted || this.companyId !== companyId) {
        throw new DOMException("Telemetry refresh cancelled", "AbortError");
      }

      const records = response.data?.records ?? response.data?.result;
      if (!Array.isArray(records)) throw new Error("Invalid telemetry fleet response");
      const existing = new Map(this.vehicles.map((vehicle) => [vehicle.id, vehicle]));
      this.vehicles = records.map((item) => {
        const incoming = normalizeFleetVehicle(item);
        const current = existing.get(incoming.id);
        if (!current) return incoming;

        // Снимок HTTP мог быть сделан до позиции, пришедшей по WebSocket.
        if (current.telemetry_tracker?.id === incoming.telemetry_tracker?.id &&
            current.telemetry_current_position !== positionsAtStart.get(incoming.id)) {
          incoming.telemetry_current_position = current.telemetry_current_position;
          incoming.freshness_status = current.freshness_status;
        }
        return Object.assign(current, incoming);
      });
    },

    async getVehiclesRequest(params = {}) {
      this.isLoading = true;
      try {
        await this.refreshVehicles({ params });
      } finally {
        this.isLoading = false;
      }
    },

    updateVehiclePosition(vehicleId, positionData) {
      const idx = this.vehicles.findIndex((v) => v.id === vehicleId);
      if (idx === -1) return;

      const updated = {
        ...this.vehicles[idx],
        freshness_status:
          positionData.freshness_status ?? this.vehicles[idx].freshness_status,
        telemetry_current_position: {
          ...this.vehicles[idx].telemetry_current_position,
          ...positionData,
        },
      };

      this.vehicles.splice(idx, 1, updated);
    },

    async fetchCalendarStats(trackerId, from, to) {
      this.calendarLoading = true;
      try {
        const response = await telemetryDailyStatsApi.calendar(
          this.companyId,
          trackerId,
          { from, to }
        );

        const map = {};
        (response.data.result || []).forEach((item) => {
          map[item.date] = item.mileage_km;
        });

        this.calendarStats = map;
      } catch (error) {
        console.error(
          "Ошибка при загрузке статистики календаря:",
          error
        );
        throw error;
      } finally {
        this.calendarLoading = false;
      }
    },

    async fetchRoutePositions({
      vehicle,
      from,
      to,
      showExcludedPoints = false,
      diagnosticMode = null,
    }) {
      this.routeLoading = true;
      this.routePositions = [];
      this.excludedRoutePositions = [];
      this.routeVehicle = vehicle;
      this.routeDateFrom = from;
      this.routeDateTo = to;

      const trackerId =
        vehicle.telemetry_current_position?.telemetry_tracker_id ??
        vehicle.telemetry_tracker?.id ??
        null;

      if (!trackerId) {
        this.routeVehicle = null;
        this.routeDateFrom = null;
        this.routeDateTo = null;
        this.routeLoading = false;
        throw new Error("У транспорта отсутствует tracker_id");
      }
      const dateTimeFrom = new Date(from + "T00:00:00");
      const dateTimeTo = new Date(to + "T23:59:59");
      const shouldUseDiagnosticMode =
        diagnosticMode ?? this.routeDiagnosticMode;

      this.routeDiagnosticMode = shouldUseDiagnosticMode;

      const params = {
        tracker_id: trackerId,
        from: dateTimeFrom,
        to: dateTimeTo,
        include_excluded_positions: showExcludedPoints,
        diagnostic_mode: shouldUseDiagnosticMode,
      };

      try {
        const response = await telemetryDailyStatsApi.positions(
          this.companyId,
          params
        );

        const positions = Array.isArray(response.data?.positions)
          ? response.data.positions
          : [];

        positions.sort(
          (a, b) =>
            new Date(a.recorded_at).getTime() -
            new Date(b.recorded_at).getTime()
        );

        this.routePositions = positions;
        this.excludedRoutePositions = Array.isArray(
          response.data?.excluded_positions
        )
          ? response.data.excluded_positions
          : [];
      } catch (error) {
        this.routeVehicle = null;
        this.routeDateFrom = null;
        this.routeDateTo = null;
        console.error("Ошибка при загрузке маршрута:", error);
        throw error;
      } finally {
        this.routeLoading = false;
      }
    },

    async massUpdateRoutePositionExclusion(positionIds, action) {
      this.routeBulkUpdateSubmitting = true;
      try {
        const response = await telemetryPositionsApi.massUpdateExclusion(
          this.companyId,
          positionIds,
          action
        );
        const updatedCount = response.data?.result?.updated_count || 0;
        const actionLabel = action === "exclude" ? "Исключено" : "Восстановлено";
        const mileageNotice =
          "Километраж может измениться в течение дня, обычно в течение часа.";

        useNotificationStore().showSuccess(
          `${actionLabel} ${updatedCount} точек. ${mileageNotice}`
        );

        return updatedCount;
      } finally {
        this.routeBulkUpdateSubmitting = false;
      }
    },

    clearRoute() {
      this.routePositions = [];
      this.excludedRoutePositions = [];
      this.routeVehicle = null;
      this.routeDateFrom = null;
      this.routeDateTo = null;
      this.routeDiagnosticMode = false;
    },

    async fetchSelectVehicles(payload = {}) {
      const searchQuery = payload.search || "";
      const requestVersion = this.selectVehiclesRequestVersion + 1;

      this.selectVehiclesSearchQuery = searchQuery;
      this.selectVehiclesRequestVersion = requestVersion;

      return await this.getSelectVehiclesRequest(1, {
        searchQuery,
        requestVersion,
      });
    },

    async loadMoreSelectVehicles() {
      if (
        this.selectVehiclesLoading ||
        !this.selectVehiclesPagination.next
      ) {
        return;
      }
      const nextPage = this.selectVehiclesPagination.page + 1;
      return await this.getSelectVehiclesRequest(nextPage, {
        searchQuery: this.selectVehiclesSearchQuery,
        requestVersion: this.selectVehiclesRequestVersion,
      });
    },

    async getSelectVehiclesRequest(
      page,
      {
        searchQuery = this.selectVehiclesSearchQuery,
        requestVersion = this.selectVehiclesRequestVersion,
      } = {}
    ) {
      const pageNumber = parseInt(page) || 1;

      try {
        this.selectVehiclesLoading = true;

        const params = { page: pageNumber };

        if (searchQuery) {
          params["q[search]"] = searchQuery;
        }

        const response = await telemetryVehiclesApi.selectOptions(
          this.companyId,
          params
        );

        // Пока ждали сеть, пользователь мог изменить строку поиска. Результат
        // такого запроса относится к предыдущему значению и больше не должен
        // менять dropdown или снимать индикатор загрузки у нового запроса.
        if (requestVersion !== this.selectVehiclesRequestVersion) return;

        const records = Array.isArray(response.data?.result)
          ? response.data.result
          : [];

        // Backend select_options уже отдает машины, доступные для выбора в мониторинге:
        // у каждой есть активный основной трекер. Текущей позиции может не быть,
        // но такая машина все равно должна выбираться для построения маршрута.
        const normalizedVehicles = records;

        if (pageNumber === 1) {
          this.selectVehicles = normalizedVehicles;
        } else {
          this.selectVehicles = [
            ...this.selectVehicles,
            ...normalizedVehicles,
          ];
        }

        const meta = response.data.pagination_meta || {
          page: 1,
          pages: 1,
          series: [],
          next: null,
          prev: null,
          count: normalizedVehicles.length,
          limit: normalizedVehicles.length || 40,
        };

        this.selectVehiclesPagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
        };
      } catch (error) {
        if (requestVersion === this.selectVehiclesRequestVersion) {
          // Новый query не может показывать результат предыдущего успешного
          // запроса. Для первой страницы очищаем список, чтобы BaseSelect
          // отобразил свой empty state вместо устаревших options.
          if (pageNumber === 1) {
            this.selectVehicles = [];
            this.selectVehiclesPagination = {
              page: 1,
              pages: 1,
              series: [],
              next: null,
              prev: null,
              count: 0,
              limit: 40,
            };
          }
          console.error(
            "Ошибка при получении транспорта для селекта:",
            error
          );
        }
      } finally {
        if (requestVersion === this.selectVehiclesRequestVersion) {
          this.selectVehiclesLoading = false;
        }
      }
    },

    clearSelectVehicles() {
      // Инвалидируем незавершенный запрос, чтобы после очистки он не вернул
      // в селект список от уже неактуального поиска.
      this.selectVehiclesRequestVersion += 1;
      this.selectVehicles = [];
      this.selectVehiclesSearchQuery = "";
      this.selectVehiclesLoading = false;
      this.selectVehiclesPagination = {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      };
    },
  },
});
